import React, { useState } from 'react';
import { resetDemoData, isDemoMode } from '../utils/localApi';
import { useAuth } from '../context/AuthContext';

/**
 * DemoBanner — 演示版说明条
 *
 * 作品集场景下，访问者需要三秒内明白三件事：
 *  1. 这是可以随便点的演示，数据只存在自己浏览器里（隐私 + 放心操作）
 *  2. 数据按账号隔离，一个账号一份数据（与正式版行为一致，只是凭证在本地）
 *  3. 演示版的边界在哪里（提醒依赖页面打开，完整版由服务端定时扫描）
 * 主动交代边界比被面试官问出来更好。
 */
export default function DemoBanner() {
  const { user } = useAuth();
  // 隐私模式下 sessionStorage 可能不可用，读取失败不应让组件崩溃
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem('demo_banner_hidden') === '1'; } catch (e) { return false; }
  });

  if (!isDemoMode || hidden) return null;

  const handleReset = () => {
    if (window.confirm('重置后当前账号将恢复为初始演示数据，你刚才的改动会丢失。确定继续吗？')) {
      resetDemoData();
      window.location.reload();
    }
  };

  const handleHide = () => {
    try { sessionStorage.setItem('demo_banner_hidden', '1'); } catch (e) {}
    setHidden(true);
  };

  return (
    <div
      style={{
        background: '#EAF3FF',
        borderBottom: '1px solid #BBD8FF',
        color: '#134A8E',
        padding: '10px 14px',
        fontSize: 15,
        lineHeight: 1.6,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        /* 这里原来写的是 position:'sticky' + top:0 + zIndex:999，已改回普通文档流。
           原因是实测出来的（scripts 之外的 sticky-check 脚本量的，2026-09-21）：
           这条说明在 375px 手机上高 **234px**，sticky 之后常驻吃掉 812px 视口的 **29%**；
           更要命的是它和药品页那条同样 `top:0` 的吸顶「添加药品」条**抢同一个位置** ——
           z999 vs z20，命中的永远是说明条，于是「吸顶条」在滚动后**整条看不见**
           （实测两者竖直重叠 98px，正好是吸顶条的全高）。
           说明条的作用是「进来看一眼就懂」，首次加载看到就够了，
           不需要一直占着屏幕 —— 改成随页面滚走，视口省下来 29%，吸顶条也真的看得见了。 */
      }}
    >
      <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>🧪 可交互演示版</span>
      <span style={{ flex: 1, minWidth: 200 }}>
        数据保存在你自己的浏览器，可放心随意点击。
        {user ? <> 当前账号 <b>{user.username}</b>，换账号登录即可看到数据是隔离的。</> : null}
        {/*
          第 3 条边界必须显示出来，不能只写在注释里。
          演示版没有后端定时任务，提醒只能靠页面轮询；不主动交代，
          访客会以为「提醒没响」是功能坏了，而不是方案取舍。
        */}
        <span style={{ display: 'block', marginTop: 2, opacity: 0.85 }}>
          提醒需页面打开时才会检查（演示版没有服务端定时任务），完整版由服务端每分钟扫描。
        </span>
      </span>
      <button
        onClick={handleReset}
        style={{
          background: '#fff', border: '1px solid #BBD8FF', color: '#134A8E',
          borderRadius: 8, padding: '6px 12px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}
      >
        重置当前账号数据
      </button>
      <button
        onClick={handleHide}
        aria-label="收起提示"
        style={{
          background: 'transparent', border: 'none', color: '#134A8E',
          fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: '0 4px',
        }}
      >
        ×
      </button>
    </div>
  );
}
